import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { Globe2, Users, Clock, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "../lib/auth";

const FEATURES = [
    {
        icon: Globe2,
        title: "Any language",
        body: "99+ languages auto-detected. From Mandarin to Maori — no language picker required.",
    },
    {
        icon: Users,
        title: "Speaker labels",
        body: "Smart diarization separates voices automatically. Up to 32 speakers, neatly labeled.",
    },
    {
        icon: Clock,
        title: "Word-level timestamps",
        body: "Every word stamped to the millisecond. Perfect for subtitles, podcasts, and citations.",
    },
];

export default function Landing() {
    const { user } = useAuth();
    const nav = useNavigate();

    return (
        <div className="min-h-screen bg-white">
            <Header />

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-60 [mask-image:radial-gradient(ellipse_at_center,white,transparent_70%)]" />
                <div className="relative max-w-4xl mx-auto px-6 pt-20 pb-24 sm:pt-28 sm:pb-32 text-center">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm fade-in-up" data-testid="hero-badge">
                        <Sparkles className="w-3.5 h-3.5 text-indigo-600" strokeWidth={2} />
                        Powered by ElevenLabs Scribe
                    </div>
                    <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-slate-900 fade-in-up" data-testid="hero-title">
                        Transcribe anything.<br />
                        <span className="text-indigo-600">Free.</span>
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto fade-in-up" data-testid="hero-subtitle">
                        Paste a link or upload a file. Get back a clean transcript with speaker labels, timestamps, and language detection — in minutes.
                    </p>

                    <div className="mt-10 max-w-2xl mx-auto fade-in-up">
                        {/* Disabled-looking input that prompts to sign in */}
                        <div
                            onClick={() => nav(user ? "/app" : "/auth")}
                            className="cursor-pointer flex items-center gap-2 rounded-2xl border border-slate-200 bg-white pl-4 pr-2 py-3 shadow-sm hover:border-indigo-500 hover:ring-4 hover:ring-indigo-500/10 transition-all group"
                            data-testid="hero-cta-input"
                        >
                            <span className="text-slate-400 text-base flex-1 text-left">
                                Paste a URL or drop a file…
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 text-white px-4 py-2 text-sm font-medium group-hover:bg-indigo-700 transition-colors">
                                {user ? "Open dashboard" : "Sign in to start"}
                                <ArrowRight className="w-4 h-4" strokeWidth={2} />
                            </span>
                        </div>
                        <p className="mt-3 text-sm text-slate-400">
                            Free tier: 20 minutes / month · Works with YouTube, TikTok, Instagram & Facebook · 99+ languages
                        </p>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="max-w-5xl mx-auto px-6 pb-24 sm:pb-32">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-widest text-center" data-testid="features-eyebrow">
                    What you get
                </p>
                <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 text-center">
                    Everything a great transcript needs.
                </h2>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
                    {FEATURES.map((f, i) => (
                        <div
                            key={f.title}
                            className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:border-slate-300 hover:-translate-y-0.5 transition-all duration-200"
                            data-testid={`feature-card-${i}`}
                        >
                            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center mb-5">
                                <f.icon className="w-5 h-5 text-indigo-600" strokeWidth={1.75} />
                            </div>
                            <h3 className="text-lg font-medium text-slate-900 tracking-tight">{f.title}</h3>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.body}</p>
                        </div>
                    ))}
                </div>

                {/* Final CTA */}
                <div className="mt-20 text-center">
                    <Link
                        to={user ? "/app" : "/auth"}
                        className="inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-6 py-3 text-base font-medium hover:bg-slate-800 transition-colors"
                        data-testid="footer-cta"
                    >
                        {user ? "Go to dashboard" : "Get started — it's free"}
                        <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </Link>
                </div>
            </section>

            <footer className="border-t border-slate-200 py-8">
                <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
                    <span>© {new Date().getFullYear()} Transcribely</span>
                    <span className="text-xs text-slate-400">Built with ElevenLabs Scribe · v2 batch</span>
                </div>
            </footer>
        </div>
    );
}
